import {
  confirmResetPassword,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
  type SignInOutput,
} from "aws-amplify/auth";

import { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "./IAuthModel";
import type {
  AuthError,
  AuthErrorCode,
  AuthTokens,
  ConfirmResetPasswordRequest,
  ConfirmSignUpRequest,
  IdentityProvider,
  ResetPasswordRequest,
  SignInRequest,
  SignInResult,
  SignUpRequest,
  UserInfo,
} from "./types";
import { configureAmplify } from "./amplifyConfig";

/**
 * `AmplifyAuthModel` — production implementation of {@link IAuthModel} on
 * top of `aws-amplify/auth` v6.
 *
 * Unity reference: `Models/AuthModel/CognitoAuthModel.cs` + `CognitoIdp.cs`.
 * We delegate token storage / refresh to Amplify (which uses secure
 * platform storage by default) instead of re-implementing Unity's
 * `PlayerPrefs`-backed cache.
 *
 * The class is intentionally thin — it normalizes Amplify's surface to
 * the Unity-style `IAuthModel` contract, translates errors to a small
 * `AuthError.code` enum, and emits the same `Emitter` events the rest of
 * the app already listens for. Anything more (multi-IdP, learner code,
 * external project credentials) can layer on top later without touching
 * callers.
 */
export class AmplifyAuthModel implements IAuthModel {
  readonly onInitialized = new Emitter<void>();
  readonly onSignedIn = new Emitter<UserInfo>();
  readonly onSignedOut = new Emitter<void>();
  readonly onTokensRefreshed = new Emitter<AuthTokens>();
  readonly onSessionExpired = new Emitter<void>();

  private m_isInitialized = false;
  private m_currentUser: UserInfo | null = null;
  private m_refreshPromise:
    | Promise<Awaited<ReturnType<typeof fetchAuthSession>>>
    | null = null;

  get isInitialized(): boolean {
    return this.m_isInitialized;
  }

  get currentUser(): UserInfo | null {
    return this.m_currentUser;
  }

  get isAuthenticated(): boolean {
    return this.m_currentUser !== null;
  }

  // Web today only ships Cognito; learner-code / OIDC modes from Unity
  // can be added as additional implementations behind the same token.
  get identityProvider(): IdentityProvider {
    return "cognito";
  }

  // ---- Lifecycle ---------------------------------------------------

  async initializeAsync(): Promise<UserInfo | null> {
    configureAmplify();
    try {
      const user = await this.loadCurrentUser();
      this.setCurrentUser(user, /*emitSignedIn*/ false);
      return user;
    } catch {
      this.setCurrentUser(null, false);
      return null;
    } finally {
      if (!this.m_isInitialized) {
        this.m_isInitialized = true;
        this.onInitialized.emit();
      }
    }
  }

  async ensureFreshTokens(): Promise<void> {
    if (!this.m_currentUser) return;

    try {
      await this.forceRefreshSession();
    } catch (err) {
      await this.expireSessionAsync("ensureFreshTokens", err);
    }
  }

  // ---- Sign-in / sign-out -----------------------------------------

  async signInAsync(req: SignInRequest): Promise<SignInResult> {
    try {
      const out: SignInOutput = await signIn({
        username: req.email.trim().toLowerCase(),
        password: req.password,
      });

      const challenge = mapSignInChallenge(out, req.email);
      if (challenge) return challenge;

      const user = await this.loadCurrentUser();
      this.setCurrentUser(user, /*emitSignedIn*/ true);
      return { kind: "signedIn", user };
    } catch (err) {
      throw toAuthError(err);
    }
  }

  async signOutAsync(): Promise<void> {
    try {
      await signOut();
    } catch (err) {
      // Treat signOut errors as "still signed out" — log only.
      console.warn("[AmplifyAuthModel] signOut failed:", err);
    } finally {
      this.setCurrentUser(null, false);
      this.onSignedOut.emit();
    }
  }

  // ---- Sign-up ----------------------------------------------------

  async signUpAsync(req: SignUpRequest): Promise<{ requiresConfirmation: boolean }> {
    try {
      const out = await signUp({
        username: req.email.trim().toLowerCase(),
        password: req.password,
        options: {
          userAttributes: {
            email: req.email.trim().toLowerCase(),
            ...(req.firstName ? { given_name: req.firstName } : {}),
            ...(req.lastName ? { family_name: req.lastName } : {}),
          },
        },
      });
      return { requiresConfirmation: !out.isSignUpComplete };
    } catch (err) {
      throw toAuthError(err);
    }
  }

  async confirmSignUpAsync(req: ConfirmSignUpRequest): Promise<void> {
    try {
      await confirmSignUp({
        username: req.email.trim().toLowerCase(),
        confirmationCode: req.code.trim(),
      });
    } catch (err) {
      throw toAuthError(err);
    }
  }

  async resendSignUpCodeAsync(email: string): Promise<void> {
    try {
      await resendSignUpCode({ username: email.trim().toLowerCase() });
    } catch (err) {
      throw toAuthError(err);
    }
  }

  // ---- Password reset ---------------------------------------------

  async resetPasswordAsync(req: ResetPasswordRequest): Promise<void> {
    try {
      await resetPassword({ username: req.email.trim().toLowerCase() });
    } catch (err) {
      throw toAuthError(err);
    }
  }

  async confirmResetPasswordAsync(req: ConfirmResetPasswordRequest): Promise<void> {
    try {
      await confirmResetPassword({
        username: req.email.trim().toLowerCase(),
        confirmationCode: req.code.trim(),
        newPassword: req.newPassword,
      });
    } catch (err) {
      throw toAuthError(err);
    }
  }

  // ---- Tokens -----------------------------------------------------

  async getIdToken(forceRefresh = false): Promise<string | null> {
    try {
      const session = forceRefresh
        ? await this.forceRefreshSession()
        : await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      if (!idToken) {
        if (this.m_currentUser) {
          throw new Error("Authenticated session did not contain an ID token");
        }
        return null;
      }
      return idToken.toString();
    } catch (err) {
      await this.expireSessionAsync("getIdToken", err);
      return null;
    }
  }

  // ---- Internals --------------------------------------------------

  private async forceRefreshSession(): Promise<
    Awaited<ReturnType<typeof fetchAuthSession>>
  > {
    if (this.m_refreshPromise) return this.m_refreshPromise;

    const refreshPromise = (async () => {
      const session = await fetchAuthSession({ forceRefresh: true });
      const tokens = extractTokens(session);
      if (!tokens) {
        throw new Error("Refreshed session did not contain Cognito tokens");
      }
      this.onTokensRefreshed.emit(tokens);
      return session;
    })();
    this.m_refreshPromise = refreshPromise;

    try {
      return await refreshPromise;
    } finally {
      if (this.m_refreshPromise === refreshPromise) {
        this.m_refreshPromise = null;
      }
    }
  }

  private async expireSessionAsync(context: string, err: unknown): Promise<void> {
    console.warn(`[AmplifyAuthModel] ${context} failed:`, err);
    if (!this.m_currentUser) return;

    this.setCurrentUser(null, false);
    try {
      await signOut();
    } catch (signOutError) {
      console.warn(
        "[AmplifyAuthModel] failed to clear the expired Amplify session:",
        signOutError,
      );
    }
    this.onSessionExpired.emit();
  }

  private async loadCurrentUser(): Promise<UserInfo> {
    const [user, attrs, session] = await Promise.all([
      getCurrentUser(),
      safeFetchUserAttributes(),
      fetchAuthSession(),
    ]);

    const idTokenPayload = session.tokens?.idToken?.payload ?? {};
    const groupsRaw = idTokenPayload["cognito:groups"];
    const groups = Array.isArray(groupsRaw)
      ? (groupsRaw.filter((g): g is string => typeof g === "string"))
      : [];

    // `custom:accountid` mirrors Unity's `user.custom_accountid` →
    // `UserInfo.CustomerID` (`Models/AuthModel/CognitoIdp.cs:424`).
    // It's the multi-tenant account id used by `getAccount` /
    // `getProjectsByCustomer` everywhere.
    const customerIdRaw = idTokenPayload["custom:accountid"];
    const customerId =
      typeof customerIdRaw === "string" && customerIdRaw.length > 0
        ? customerIdRaw
        : null;

    // `cognito:username` mirrors Unity's `UserInfo.UserID`
    // (`CognitoIdp.cs:423` `userID = user.cognito_username`). It's
    // also the exact value Unity writes into `ProjectData.CreatorId`,
    // so the drafts/shared filter (`ProjectsViewModel.cs:3514`)
    // depends on this — NOT on Amplify's `user.userId` (which is the
    // Cognito `sub` UUID and never matches `CreatorId`). Falls back
    // to `sub` only if the claim is missing for some reason.
    const cognitoUsernameRaw = idTokenPayload["cognito:username"];
    const userId =
      typeof cognitoUsernameRaw === "string" && cognitoUsernameRaw.length > 0
        ? cognitoUsernameRaw
        : user.userId;

    const email = attrs.email ?? user.signInDetails?.loginId ?? user.username;
    const firstName = attrs.given_name;
    const lastName = attrs.family_name;
    const displayName = buildDisplayName(firstName, lastName, email);

    return {
      userId,
      customerId,
      email: email.trim().toLowerCase(),
      firstName,
      lastName,
      displayName,
      nameTag: buildNameTag(firstName, lastName, displayName),
      groups,
      // `CognitoIdp.cs:419` — the group names are matched exactly, and the
      // claim is already in hand, so this costs no request.
      isAdmin: groups.some((g) => g === "SuperAdmins" || g === "Admins"),
      // Unity sets this only from the learner-code auth model
      // (`LearnerCodeAuthModel.cs:156`); this implementation is Cognito-only,
      // so it is always false here. A learner-code model would pass true.
      isSingleUser: this.identityProvider === "learnerCode",
    };
  }

  private setCurrentUser(user: UserInfo | null, emitSignedIn: boolean): void {
    this.m_currentUser = user;
    if (user && emitSignedIn) this.onSignedIn.emit(user);
  }
}

// Helpers ------------------------------------------------------------

async function safeFetchUserAttributes(): Promise<{
  email?: string;
  given_name?: string;
  family_name?: string;
}> {
  try {
    return (await fetchUserAttributes()) as {
      email?: string;
      given_name?: string;
      family_name?: string;
    };
  } catch {
    return {};
  }
}

function buildDisplayName(
  firstName: string | undefined,
  lastName: string | undefined,
  email: string,
): string {
  const full = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  if (full.length > 0) return full;
  const local = email.split("@")[0];
  return local || email;
}

/**
 * Port of `UserInfo.NameTag` (`Models/AuthModel/UserInfo.cs:350`):
 * `"{firstName}"` plus `" {lastName[0]}"` when a surname exists. Unity has no
 * fallback and yields an empty tag for a user with no given name; the web
 * falls back to `displayName` so presence tags and `AssetData.UserTag` are
 * never blank.
 */
function buildNameTag(
  firstName: string | undefined,
  lastName: string | undefined,
  displayName: string,
): string {
  if (firstName && lastName) return `${firstName} ${lastName[0]}`;
  if (firstName) return firstName;
  return displayName;
}

function mapSignInChallenge(
  out: SignInOutput,
  email: string,
): SignInResult | null {
  if (out.isSignedIn) return null;
  switch (out.nextStep.signInStep) {
    case "CONFIRM_SIGN_UP":
      return { kind: "confirmSignUp", email };
    case "RESET_PASSWORD":
      return { kind: "resetPassword", email };
    case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
      return { kind: "newPasswordRequired" };
    case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
    case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
    case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
    case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION":
      return { kind: "mfaRequired" };
    default:
      // "DONE" without isSignedIn is unexpected; surface as wrong-creds.
      return null;
  }
}

function extractTokens(session: Awaited<ReturnType<typeof fetchAuthSession>>): AuthTokens | null {
  const idToken = session.tokens?.idToken;
  const accessToken = session.tokens?.accessToken;
  if (!idToken || !accessToken) return null;
  const expRaw = idToken.payload?.exp;
  const expiresAt = typeof expRaw === "number" ? expRaw : 0;
  return {
    idToken: idToken.toString(),
    accessToken: accessToken.toString(),
    expiresAt,
  };
}

function toAuthError(err: unknown): AuthError {
  const name = (err as { name?: unknown })?.name;
  const message = (err as { message?: unknown })?.message;
  const safeMessage =
    typeof message === "string" && message.length > 0
      ? message
      : "Authentication failed";
  const code = mapAuthErrorCode(typeof name === "string" ? name : "");
  return { code, message: safeMessage, cause: err };
}

function mapAuthErrorCode(name: string): AuthErrorCode {
  switch (name) {
    case "NotAuthorizedException":
      return "wrong-credentials";
    case "UserNotFoundException":
      return "user-not-found";
    case "UserNotConfirmedException":
      return "user-not-confirmed";
    case "LimitExceededException":
    case "TooManyRequestsException":
    case "TooManyFailedAttemptsException":
      return "too-many-attempts";
    case "ExpiredCodeException":
      return "code-expired";
    case "CodeMismatchException":
      return "code-mismatch";
    case "InvalidPasswordException":
      return "password-policy";
    case "UsernameExistsException":
      return "user-already-exists";
    case "NetworkError":
    case "TypeError":
      return "network";
    default:
      return "unknown";
  }
}
